/**
 * ReprocessMenu — reusable dropdown for bulk rerun of Bill2Rollin jobs.
 *
 * Props:
 *   accountId:   required when scope != 'all'
 *   selectedIds: string[] (for scope=selected)
 *   onDone:      callback after successful rerun
 *   className:   optional wrapper class
 */
import { useState } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { Loader2, RefreshCw, ChevronDown, ScanLine, AlertCircle, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function ReprocessMenu({
  accountId,
  selectedIds = [],
  onDone,
  className = '',
}) {
  const [loading, setLoading] = useState(false);

  const run = async (scope) => {
    if (!accountId) { toast.error('Organization required'); return; }
    if (scope === 'selected' && selectedIds.length === 0) {
      toast.error('Select at least one job first');
      return;
    }
    const label = scope === 'selected' ? `${selectedIds.length} selected` : scope;
    if (!window.confirm(`Reprocess ${label} job(s)? Extraction will re-run with the latest self-validation logic.`)) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/bill2rollin/jobs/bulk-rerun?account_id=${accountId}`, {
        scope,
        job_ids: scope === 'selected' ? selectedIds : null,
      });
      toast.success(
        `Dispatched ${data.dispatched_count} / ${data.candidate_count} (${data.skipped_count} skipped)`,
        {
          description: data.skipped_count
            ? `Skipped: ${data.skipped.slice(0, 3).map(s => `${s.reason}`).join(', ')}`
            : undefined,
        }
      );
      if (onDone) onDone(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reprocess failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={loading || !accountId}
          className={`h-8 text-xs ${className}`}
          data-testid="reprocess-menu-btn"
        >
          {loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
          Reprocess
          <ChevronDown className="h-3 w-3 ml-1 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem
          onClick={() => run('selected')}
          disabled={selectedIds.length === 0}
          data-testid="reprocess-selected"
        >
          <Check className="h-3.5 w-3.5 mr-2 text-blue-600" />
          <div className="flex-1">
            <div className="text-xs font-medium">Reprocess Selected</div>
            <div className="text-[10px] text-muted-foreground">{selectedIds.length} chosen</div>
          </div>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => run('suspects')} data-testid="reprocess-suspects">
          <ScanLine className="h-3.5 w-3.5 mr-2 text-amber-600" />
          <div>
            <div className="text-xs font-medium">Reprocess Suspects</div>
            <div className="text-[10px] text-muted-foreground">Flagged bad extractions only</div>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => run('failed')} data-testid="reprocess-failed">
          <AlertCircle className="h-3.5 w-3.5 mr-2 text-red-600" />
          <div>
            <div className="text-xs font-medium">Reprocess Failed</div>
            <div className="text-[10px] text-muted-foreground">failed / timeout jobs</div>
          </div>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => run('all')} data-testid="reprocess-all" className="text-red-600 focus:text-red-600">
          <RefreshCw className="h-3.5 w-3.5 mr-2" />
          <div>
            <div className="text-xs font-medium">Reprocess ALL</div>
            <div className="text-[10px] text-muted-foreground/90">every job (heavy!)</div>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
